<template>
  <div class="settings-users">
    <div v-if="!loading">
      <table-layout
        pkField="login"
        :selected.sync="selected"
        :isAdmin="isAdmin"
        :loading="loading"
        :items="usersModel"
        :columns="columns"
        :addOptions="['addNewUser']"
        @click:addNew="addNewUser"
        @open-modal="openUpdateModal"
      >
        <template #actions>
          <b-dropdown-item @click="openDialogRemove" aria-role="listitem">{{
            $t('server.actions.delete')
          }}</b-dropdown-item>
        </template>
      </table-layout>
    </div>
    <loader v-else></loader>
    <b-modal
      :active.sync="isUpdateUser"
      has-modal-card
      trap-focus
      :destroy-on-hide="false"
      aria-role="dialog"
      aria-modal
      class="modal-layout"
    >
      <div class="modal-card" style="width: auto">
        <section class="modal-card-body">
          <h1>{{ $t('settings.user.update') }}</h1>
          <parametrs-list :items.sync="updateUserData" :parameters="updateUserFields"> </parametrs-list>
          <edit-button
            v-if="!isUpdateUserLoading"
            :editOptions="['save']"
            @click="updateUser"
            :disabled="!updateUserData.name"
          >
            <template>
              <span class="text">{{ $t('button.update') }}</span>
            </template>
          </edit-button>
          <loader v-else></loader>
        </section>
      </div>
    </b-modal>
    <b-modal
      :active.sync="isAddUser"
      has-modal-card
      trap-focus
      :destroy-on-hide="false"
      aria-role="dialog"
      aria-modal
      class="modal-layout"
    >
      <div class="modal-card" style="width: auto">
        <section class="modal-card-body">
          <h1>{{ $t('settings.user.add_new') }}</h1>
          <parametrs-list :items.sync="newUserData" :parameters="newUserFields"> </parametrs-list>
          <edit-button
            v-if="!isAddUserLoading"
            :editOptions="['save']"
            @click="saveNewUser"
            :disabled="!newUserData.login || !newUserData.password || !newUserData.name"
          >
            <template>
              <span class="text">{{ $t('button.add.title') }}</span>
            </template>
          </edit-button>
          <loader v-else></loader>
        </section>
      </div>
    </b-modal>
  </div>
</template>

<script>
import TableLayout from '@/layout/TableLayout'
import Loader from '@/components/Loader'
import EditButton from '@/components/EditButton'
import ParametrsList from '@/components/ParametrsList'

export default {
  components: {
    TableLayout,
    Loader,
    EditButton,
    ParametrsList
  },
  props: ['users', 'loading', 'isAdmin'],
  data() {
    return {
      isUpdateUser: false,
      isUpdateUserLoading: false,
      isAddUser: false,
      isAddUserLoading: false,
      usersModel: [],
      selected: {},
      columns: [
        {
          sortable: true,
          field: 'login',
          label: 'settings.user.login_table',
          modal: {
            type: 'login',
            field: 'login'
          }
        },
        {
          sortable: true,
          field: 'name',
          label: 'settings.user.name'
        },
        {
          sortable: true,
          field: 'role',
          label: 'settings.user.role'
        }
      ],
      newUserData: {
        login: '',
        password: '',
        role: 'USER',
        name: ''
      },
      newUserFields: [
        {
          name: 'login',
          data_type: 'STRING',
          description: this.$t('settings.user.login'),
          autocomplete: 'off'
        },
        {
          name: 'password',
          data_type: 'PASSWORD',
          description: this.$t('settings.user.password'),
          autocomplete: 'off'
        },
        {
          name: 'name',
          data_type: 'STRING',
          description: this.$t('settings.user.name')
        },
        {
          name: 'role',
          data_type: 'SELECT',
          description: this.$t('settings.user.select_role'),
          options: [
            {
              value: 'ADMIN',
              label: 'ADMIN'
            },
            {
              value: 'USER',
              label: 'USER'
            }
          ]
        }
      ],
      updateUserData: {},
      updateUserFields: [
        {
          name: 'login',
          data_type: 'DISABLED',
          description: this.$t('settings.user.login'),
          autocomplete: 'off'
        },
        {
          name: 'password',
          data_type: 'PASSWORD',
          description: this.$t('settings.user.password'),
          autocomplete: 'off'
        },
        {
          name: 'name',
          data_type: 'STRING',
          description: this.$t('settings.user.name')
        },
        {
          name: 'role',
          data_type: 'SELECT',
          description: this.$t('settings.user.select_role'),
          options: [
            {
              value: 'ADMIN',
              label: 'ADMIN'
            },
            {
              value: 'USER',
              label: 'USER'
            }
          ]
        }
      ]
    }
  },
  methods: {
    openUpdateModal(item) {
      this.isUpdateUser = true
      this.updateUserData = item.value
    },
    addNewUser() {
      this.isAddUser = true
    },
    updateUser() {
      const url = `${process.env.VUE_APP_API_ENDPOINT || window.location.origin}/api/settings/user`
      this.isUpdateUserLoading = true
      const name = this.updateUserData.name
      return fetch(url, {
        credentials: 'include',
        body: JSON.stringify({
          user_config: this.updateUserData
        }),
        method: 'POST'
      })
        .then(res => {
          if (!res.ok) {
            throw res.json()
          }
          return res.json()
        })
        .then(() => {
          this.$emit('refetch-users')
          this.notifyUser({
            name: name,
            status: 'updated'
          })
        })
        .catch(error =>
          error.then(data => {
            this.$notify({
              message: JSON.parse(data.message).message,
              type: 'danger'
            })
          })
        )
        .finally(() => {
          this.isUpdateUserLoading = false
          this.isUpdateUser = false
        })
    },
    saveNewUser() {
      const url = `${process.env.VUE_APP_API_ENDPOINT || window.location.origin}/api/settings/users`
      this.isAddUserLoading = true
      const name = this.newUserData.name
      return fetch(url, {
        credentials: 'include',
        body: JSON.stringify({
          user_config: this.newUserData
        }),
        method: 'POST'
      })
        .then(res => {
          if (!res.ok) {
            throw res.json()
          }
          return res.json()
        })
        .then(() => {
          this.notifyUser({
            name: name,
            status: 'added'
          })
          this.$emit('refetch-users')
        })
        .catch(error => {
          return error.then(data => {
            this.$notify({
              message: JSON.parse(data.message).message,
              type: 'danger'
            })
          })
        })
        .finally(() => {
          this.isAddUserLoading = false
          this.isAddUser = false
        })
    },
    setUsers(newUsers) {
      if (newUsers) {
        this.usersModel = this.$lodash.cloneDeep(newUsers)
      }
    },
    removeUsers(logins) {
      const url = `${process.env.VUE_APP_API_ENDPOINT || window.location.origin}/api/settings/users/delete`
      return fetch(url, {
        credentials: 'include',
        body: JSON.stringify({
          user_logins: logins
        }),
        method: 'POST'
      })
        .then(res => {
          if (!res.ok) {
            throw res.json()
          }
          return res.json()
        })
        .then(() => {
          const indexSet = new Set(logins)
          this.usersModel = this.usersModel.filter(value => {
            return !indexSet.has(value.login)
          })
          this.selected = []
        })
    },
    openDialogRemove() {
      if (!this.selected.length) {
        this.$notify({
          message: this.$t('notification.unselected'),
          type: 'danger'
        })
        return
      }
      const indexSet = new Set(this.selected)
      const users = this.usersModel
        .filter(value => {
          return indexSet.has(value.login)
        })
        .map(d => {
          return d['name']
        })
      const msg = users
        .map(d => {
          return d
        })
        .join(', ')
      this.$dialog.confirm({
        message: `${this.$t('confirm.remove', {
          msg: msg
        })}?`,
        onConfirm: () => {
          this.removeUsers(this.selected)
            .then(() => {
              this.notifyUser({
                name: msg,
                status: 'deleted'
              })
            })
            .catch(error =>
              error.then(data => {
                this.$notify({
                  message: JSON.parse(data.message).message,
                  type: 'danger'
                })
              })
            )
            .finally(() => {
              this.serversLoading = false
            })
        }
      })
    },
    notifyUser(variables) {
      this.$notify({
        message: `${this.$t('notification.userWas', {
          user: variables.name,
          status: this.$t(`notification.user.${variables.status}`)
        })}.`,
        type: 'success'
      })
    }
  },
  watch: {
    users: {
      immediate: true,
      deep: true,
      handler: 'setUsers'
    }
  }
}
</script>

<style lang="scss" scoped>
.settings-users {
  .modal-layout {
    .edit-button {
      float: right;
      margin-top: 1rem;
    }
  }
}
</style>
